import { useCallback, useEffect, useMemo, useState } from "react";
import { EngagementService } from "@services/engagements/engagementService";
import { KeyDateService } from "@services/engagements/keyDateService";
import { workflowEvents } from "src/modules/engagement/workflow/workflowEvents";
import type { KeyDate, KeyDateType } from "@models/keyDate.model";
import KeyDateModal from "./KeyDateModal";
import ModalDelete from "src/components/common/ModalDelete";
import InfoTip from "@/shared/components/infoTip/InfoTip";

type Props = {
  /** Campaign-scoped since MPN-2185. The server falls back to the objective's key dates
      for a campaign the backfill has not reached, so this reads correctly either way. */
  launchId: number;
};

export function CampaignKeyDatesCard({ launchId }: Props) {
  const [keyDates, setKeyDates] = useState<KeyDate[]>([]);
  const [keyDateTypes, setKeyDateTypes] = useState<KeyDateType[]>([]);
  const [editing, setEditing] = useState<KeyDate | "new" | null>(null);
  const [deleting, setDeleting] = useState<KeyDate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const service = useMemo(() => new EngagementService(), []);
  const keyDateService = useMemo(() => new KeyDateService(), []);

  const load = useCallback(async () => {
    if (!launchId) {
      setKeyDates([]);
      return;
    }
    const dates = await keyDateService.listForCampaign(launchId);
    setKeyDates(
      [...dates].sort((a, b) => new Date(a.date ?? 0).getTime() - new Date(b.date ?? 0).getTime()),
    );
  }, [keyDateService, launchId]);

  useEffect(() => {
    let cancelled = false;
    void Promise.allSettled([load(), service.getKeyDateTypes()]).then(([, types]) => {
      if (cancelled) return;
      if (types.status === "fulfilled") setKeyDateTypes(types.value ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [load, service]);

  const handleDelete = async () => {
    if (deleting?.id == null) return;
    setIsDeleting(true);
    try {
      await keyDateService.deleteForCampaign(launchId, deleting.id);
      await load();
      workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    } finally {
      setIsDeleting(false);
      setDeleting(null);
    }
  };

  return (
    <>
      <div className="campaign-label mt-4">
        Key dates
        <InfoTip
          label="Key dates"
          text="Any key date here can trigger a touch in any Email and SMS sequence. The same dates feed Schedule and Monitor."
        />
      </div>
      <div className="campaign-card">
        <div className="obj-sec__head">
          <span className="obj-sec__title">
            {keyDates.length || "No"} {keyDates.length === 1 ? "date" : "dates"}
          </span>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm ms-auto"
            style={{ fontSize: "0.72rem" }}
            disabled={!launchId}
            onClick={() => setEditing("new")}
          >
            + Key date
          </button>
        </div>
        {keyDates.length === 0 ? (
          <div className="campaign-section__empty">
            Nothing dated yet. Key dates are the milestones the campaigns are planned around.
          </div>
        ) : (
          keyDates.map((keyDate) => (
            <div className="obj-date" key={keyDate.id}>
              <span className="obj-date__when">{formatKeyDate(keyDate.date)}</span>
              <span className="obj-date__what">
                {keyDate.label?.trim() || keyDate.date_type || "Untitled date"}
              </span>
              {keyDate.is_custom && (
                <span className="campaign-pill campaign-pill--muted">Custom</span>
              )}
              <button
                type="button"
                className="obj-date__action ms-auto"
                onClick={() => setEditing(keyDate)}
              >
                Edit
              </button>
              <button
                type="button"
                className="obj-date__action obj-date__action--danger"
                onClick={() => setDeleting(keyDate)}
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>

      {editing && (
        <KeyDateModal
          launchId={launchId}
          keyDate={editing === "new" ? null : editing}
          keyDateTypes={keyDateTypes}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
            workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
          }}
        />
      )}

      {deleting && (
        <ModalDelete
          visible
          title="key date"
          objectLabel={
            deleting.label?.trim() || deleting.date_type || formatKeyDate(deleting.date)
          }
          isLoading={isDeleting}
          toggleVisibility={() => setDeleting(null)}
          deleteFn={handleDelete}
        />
      )}
    </>
  );
}

function formatKeyDate(value: string | null | undefined): string {
  if (!value) return "No date";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "No date";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default CampaignKeyDatesCard;
